// frontend/src/context/AuthContext.js
import React, { createContext, useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/api";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const authStorage = typeof window !== "undefined" ? window.sessionStorage : null;
  const shouldTryProfileBootstrap = (() => {
    if (typeof window === "undefined") return false;
    const path = String(window.location.pathname || "").toLowerCase();
    const hasSessionToken = Boolean(authStorage?.getItem("token"));
    const isProtectedPath =
      /^\/admin(\/|$)/.test(path) ||
      /^\/player(\/|$)/.test(path) ||
      /^\/coach(\/|$)/.test(path) ||
      path === "/change-password";

    // On public pages without an auth marker, skip bootstrap call to avoid
    // unnecessary /auth/profile 401 noise in browser console.
    return hasSessionToken || isProtectedPath;
  })();

  // initialize once from sessionStorage
  const initToken = (() => {
    if (!authStorage) return null;
    return authStorage.getItem("token");
  })();
  const initUser = (() => {
    try {
      const raw = authStorage ? authStorage.getItem("user") : null;
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const initRole = (() => {
    try {
      return (authStorage ? authStorage.getItem("role") : null) || initUser?.role || null;
    } catch {
      return initUser?.role || null;
    }
  })();

  const [token, setTokenState] = useState(initToken);
  const [user, setUserState] = useState(initUser);
  const [role, setRoleState] = useState(initRole ? initRole.toString().toLowerCase() : null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("role");
    } catch {}
  }, []);

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      if (token && user) {
        setLoading(false);
        return;
      }

      if (!shouldTryProfileBootstrap) {
        setLoading(false);
        return;
      }

      try {
        const res = await API.fetchAuthProfile();
        const profile = res?.data || null;
        if (!cancelled && profile?.id) {
          setToken("cookie-auth");
          setUser(profile);
          setRole(profile.role);
        }
      } catch (_e) {
        if (!cancelled) {
          setToken(null);
          setUser(null);
          setRole(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // idempotent setters that also persist to sessionStorage
  const setToken = useCallback((t) => {
    if (t === token) return;
    setTokenState(t || null);
    try {
      if (t) authStorage?.setItem("token", t);
      else authStorage?.removeItem("token");
    } catch {}
  }, [authStorage, token]);

  const setRole = useCallback((r) => {
    const norm = r ? r.toString().toLowerCase() : null;
    if (norm === role) return;
    setRoleState(norm);
    try {
      if (norm) authStorage?.setItem("role", norm);
      else authStorage?.removeItem("role");
    } catch {}
  }, [authStorage, role]);

  const setUser = useCallback((u) => {
    // avoid update if same id
    const curId = user?.id;
    const newId = u?.id;
    if (curId && newId && curId === newId) return;
    setUserState(u || null);
    try {
      if (u) authStorage?.setItem("user", JSON.stringify(u));
      else authStorage?.removeItem("user");
    } catch {}
  }, [authStorage, user?.id]);

  // ensure axios header is set when token changes (single effect)
  useEffect(() => {
    try {
      if (token && token !== "cookie-auth") {
        API.defaults = API.defaults || {};
        API.defaults.headers = API.defaults.headers || {};
        API.defaults.headers.common = API.defaults.headers.common || {};
        API.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      } else {
        if (API.defaults?.headers?.common) delete API.defaults.headers.common["Authorization"];
      }
    } catch (e) {
      // ignore
    }
  }, [token]);

  // login: call backend and persist (keeps behaviour similar to yours)
  const login = useCallback(async (identifier, password, targetRole = null) => {
    try {
      const payload = {
        username: identifier,
        password,
      };
      if (targetRole) {
        payload.target_role = targetRole;
      }

      // A rejected login must remain on this screen so the user can see the
      // error or choose a role; it must not trigger the global 401 redirect.
      const res = await API.post("/login", payload, { skipAuthRedirect: true });

      if (res?.requires_role_selection) {
        return {
          success: false,
          requires_role_selection: true,
          available_roles: res.available_roles || [],
          user: res.user || null,
          message: res.message || "Select which role to log in as",
        };
      }

      if (!res?.user) {
        throw new Error("Invalid login response");
      }

      const authToken = res.token || "cookie-auth";
      authStorage?.setItem("token", authToken);
      authStorage?.setItem("user", JSON.stringify(res.user));
      authStorage?.setItem("role", res.user.role);

      setToken(authToken);
      setUser(res.user);
      setRole(res.user.role);

      const role = res.user.role;
      if (role === "admin" || role === "secretary") navigate("/admin");
      else if (role === "district") navigate("/district");
      else if (role === "player") navigate("/player");
      else if (role === "coach") navigate("/coach");
      else if (role === "manager") navigate("/manager");
      else navigate("/");

      return { success: true, role: res.user.role, user: res.user };
    } catch (err) {
      const validationMessage =
        err?.errors && typeof err.errors === "object"
          ? Object.values(err.errors).flat().join(" ")
          : "";
      const message =
        (typeof err === "string" ? err : err?.message) ||
        err?.error ||
        validationMessage ||
        "Login failed";

      return {
        success: false,
        message,
      };
    }
  }, [authStorage, navigate, setRole, setToken, setUser]);

  const coachLogin = useCallback(async (identifier, password, targetRole = null) => {
    try {
      const payload = {
        username: identifier,
        password,
      };
      if (targetRole) {
        payload.target_role = targetRole;
      }

      const res = await API.post("/coach/login", payload);

      if (res?.requires_role_selection) {
        return {
          success: false,
          requires_role_selection: true,
          available_roles: res.available_roles || [],
          user: res.user || null,
          message: res.message || "Select which role to log in as",
        };
      }

      if (!res?.user) {
        throw new Error("Invalid coach login response");
      }

      const authToken = res.token || "cookie-auth";
      const resolvedRole = (res.user.role || (targetRole || "coach")).toLowerCase();
      authStorage?.setItem("token", authToken);
      authStorage?.setItem("user", JSON.stringify(res.user));
      authStorage?.setItem("role", resolvedRole);

      setToken(authToken);
      setUser(res.user);
      setRole(resolvedRole);

      if (resolvedRole === "player") {
        navigate("/player", { replace: true });
      } else {
        navigate("/coach", { replace: true });
      }
      return { success: true, role: resolvedRole, user: res.user };
    } catch (err) {
      const validationMessage =
        err?.errors && typeof err.errors === "object"
          ? Object.values(err.errors).flat().join(" ")
          : "";
      const message =
        (typeof err === "string" ? err : err?.message) ||
        err?.error ||
        validationMessage ||
        "Coach login failed";

      return {
        success: false,
        message,
      };
    }
  }, [authStorage, navigate, setRole, setToken, setUser]);

  const logout = useCallback(async () => {
    try {
      await API.logout();
    } catch (_e) {}
    setToken(null);
    setUser(null);
    setRole(null);
    try {
      if (API.defaults?.headers?.common) delete API.defaults.headers.common["Authorization"];
    } catch {}
    authStorage?.removeItem("token");
    authStorage?.removeItem("user");
    authStorage?.removeItem("role");
    navigate("/login", { replace: true });
  }, [authStorage, navigate, setRole, setToken, setUser]);

  const contextValue = useMemo(() => {
    return { token, user, role, loading, login, coachLogin, logout, setUser, setRole, setToken };
  }, [token, user, role, loading, login, coachLogin, logout, setUser, setRole, setToken]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
